// Pure helpers for the Calendar "Llamadas pendientes" tray (live-handoff v3).
// The API is the authority: these only shape what the server already decided.
import { branchLabel, requestedSiteBranch } from './calendarTags';

export const UNRESOLVED = ['open', 'assigned'];
export const CLOSE_STATES = ['attended', 'no_answer', 'cancelled'];
export const MIN_ATTEMPTS_FOR_NO_ANSWER = 2;

export const nonemptyId = (value) => typeof value === 'string' && value.trim().length > 0;

// Every signed-in, active user of a tenant sees the tray; the server decides the scope.
export function followupIdentity(user) {
  if (!user || !nonemptyId(user.tenant_id) || !nonemptyId(user.user_id || user.id)) return null;
  if (['inactive', 'disabled'].includes(user.status) || user.enabled === false) return null;
  return JSON.stringify({ user_id: user.user_id || user.id, tenant_id: user.tenant_id, role: user.role || '' });
}

export const isUnresolved = (row) => UNRESOLVED.includes(String(row?.status || ''));

const parseIso = (value) => {
  const ms = Date.parse(String(value || ''));
  return Number.isNaN(ms) ? null : ms;
};

export function targetFor(row) {
  return row?.status === 'open' ? row?.claim_target : row?.followup_target;
}

export function isOverdue(row, now = Date.now()) {
  if (row?.overdue === true) return true;
  const target = parseIso(targetFor(row));
  return target !== null && target <= now;
}

// Overdue first, then oldest first (age). Stable for equal keys.
export function sortQueue(rows, now = Date.now()) {
  return [...(rows || [])].map((row, index) => ({ row, index }))
    .sort((a, b) => {
      const o = Number(!isOverdue(a.row, now)) - Number(!isOverdue(b.row, now));
      if (o !== 0) return o;
      const c = String(a.row.created_at || '').localeCompare(String(b.row.created_at || ''));
      return c !== 0 ? c : a.index - b.index;
    }).map(({ row }) => row);
}

export function ageSeconds(row, now = Date.now()) {
  if (Number.isInteger(row?.age_seconds)) return row.age_seconds;
  const created = parseIso(row?.created_at);
  return created === null ? 0 : Math.max(0, Math.floor((now - created) / 1000));
}

export function formatAge(seconds) {
  const s = Math.max(0, Number(seconds) || 0);
  if (s < 60) return '<1m';
  if (s < 3600) return Math.floor(s / 60) + 'm';
  if (s < 86400) return Math.floor(s / 3600) + 'h ' + Math.floor((s % 3600) / 60) + 'm';
  return Math.floor(s / 86400) + 'd ' + Math.floor((s % 86400) / 3600) + 'h';
}

export function formatWhen(value, locale = 'es') {
  const ms = parseIso(value);
  if (ms === null) return '';
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(ms));
}

// Oct 18 (Roger 23:34): "última hoy 23:30" / "última ayer 15:49" / "última 16 oct, 15:49".
// Returns { key: 'today'|'yesterday'|'date', time, date } so the caller picks the i18n string.
export function relativeWhen(value, locale = 'es', now = Date.now()) {
  const ms = parseIso(value);
  if (ms === null) return null;
  const then = new Date(ms);
  const ref = new Date(now);
  const time = new Intl.DateTimeFormat(locale, { hour: '2-digit', minute: '2-digit' }).format(then);
  const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
  if (sameDay(then, ref)) return { key: 'today', time, date: formatWhen(value, locale) };
  const yesterday = new Date(ref); yesterday.setDate(ref.getDate() - 1);
  if (sameDay(then, yesterday)) return { key: 'yesterday', time, date: formatWhen(value, locale) };
  return { key: 'date', time, date: formatWhen(value, locale) };
}

// Oct 18 (Roger 23:34): the sucursal NAME ("Comalcalco"), never the B.PRO code GCGMCOMALCALCO.
// The API's ``branch_label`` wins; an older API that only sends the code gets a readable fallback
// (strip the GCGM dealer prefix, title-case). '' when the row has no branch.
// P1 postventa cleanup (oct22): a KNOWN branch db reads with the same label the Calendar chips
// use (#2214 calendarTags.branchLabel: GCGMVILLA -> "Villahermosa: Deportiva"), so the request
// tray, the calendar and the cartera never name one sucursal three ways.
// Jocelin 12:48 CST oct22 (#2229, "Solo hay taller en Deportiva. Tabasco 2000 solo es punto de
// venta"): there is no Tabasco 2000 service site, so a `requested_site` on the row never makes a
// label of its own -- the request reads as the taller it was written against ("Villahermosa:
// Deportiva"), exactly like the calendar (calendarTags.deriveSucursalKey); a row with NO branch of
// its own but a known requested_site folds into that site's taller db (requestedSiteBranch).
// (#2228 and #2229 crossed: #2228 called the #2214 `requestedSiteKey` that #2229 removed, so the
// merged tray threw "requestedSiteKey is not a function" on every row -- this is the reconcile.)
export function branchName(row) {
  const raw = String(row?.branch_id || '').trim() || requestedSiteBranch(row || {}) || '';
  const known = raw ? branchLabel(raw) : '';
  if (raw && known && known !== raw) return known;
  const label = String(row?.branch_label || '').trim();
  if (label) return label;
  if (!raw) return '';
  if (/^[A-Z0-9_]{6,}$/.test(raw)) {
    const stripped = raw.replace(/^GCGM/, '').replace(/_/g, ' ').toLowerCase();
    return stripped ? stripped.charAt(0).toUpperCase() + stripped.slice(1) : raw;
  }
  return raw;
}

export function followupErrorKey(error) {
  const status = error?.response?.status;
  const detail = String(error?.response?.data?.detail || '');
  if (detail === 'attempts_required') return 'attemptsRequired';
  if (detail === 'reason_required') return 'reasonRequired';
  if (detail === 'followup_assignee_ineligible') return 'assigneeIneligible';
  // Oct 16 "Volver a llamar": the server re-dispatches through the guarded single-call path.
  if (detail === 'followup_redial_unavailable' || status === 501) return 'redialUnavailable';
  if (detail === 'followup_redial_missing_agent_or_phone') return 'redialMissing';
  if (detail === 'followup_redial_failed') return 'redialFailed';
  // A 409 that is NOT a version conflict is a dial guard (STOP / call hours / stop-contact).
  if (status === 409 && detail && detail !== 'version_conflict') return 'redialBlocked';
  if (status === 401 || status === 403) return 'denied';
  if (status === 404) return 'notFound';
  if (status === 409) return 'stale';
  if (status === 422) return 'invalid';
  if (status === 503) return 'unavailable';
  return 'requestFailed';
}

export function canCloseNoAnswer(row) {
  return isUnresolved(row) && Number(row?.attempt_count || 0) >= MIN_ATTEMPTS_FOR_NO_ANSWER;
}

// Oct 16 (Roger 15:52): "El cliente dijo" must be the customer's own sentence, never the
// classifier placeholder ``outcome=callback_scheduled (post_call_analysis)`` that legacy rows carry.
export const isMachineHeard = (text) => /^\s*outcome\s*=/i.test(String(text || ''));
export function customerSaid(row) {
  const said = String(row?.customer_said || '').trim();
  if (said && !isMachineHeard(said)) return said;
  const heard = String(row?.consent?.heard || '').trim();
  return heard && !isMachineHeard(heard) ? heard : '';
}

// Oct 18 (Roger 23:34): when the row carries no customer sentence (rows written before the
// Oct 16 detector, or a consent path that stored nothing), take it from the call transcript
// the detail already loads: the last caller turn that names the window / hours / price / an
// advisor / a call-back, else the last caller turn with >= 3 words. Mirrors
// callback_followup.customer_sentence; never the classifier label; '' when nothing usable.
const CUSTOMER_SAID_RE = /\btard(?:e|ecita)\b|\bma(?:ñ|n)an(?:a|ita)\b|\bhorario\w*\b|\bprecio\b|\bcosto\b|\bcotiza\w*|\basesor\w*\b|\bejecutiv\w*\b|\bllam\w*\b|\bmarqu\w*\b|\bcomuni\w*\b|\bdespu[eé]s\b|\bantes\b/i;
const USER_ROLES = ['user', 'customer', 'caller', 'cliente', 'human'];
export function transcriptTurns(transcript) {
  let list = transcript;
  if (typeof list === 'string') { try { list = JSON.parse(list || '[]'); } catch (e) { return []; } }
  if (!Array.isArray(list)) return [];
  return list.filter((e) => e && typeof e === 'object').map((e) => ({
    role: String(e.role || e.speaker || '').trim().toLowerCase(),
    text: String(e.text || e.content || '').trim(),
  })).filter((e) => e.text);
}
export function customerSentenceFromTranscript(transcript) {
  const caller = transcriptTurns(transcript).filter((e) => USER_ROLES.includes(e.role)).map((e) => e.text)
    .filter((text) => !isMachineHeard(text));
  for (let i = caller.length - 1; i >= 0; i -= 1) {
    if (CUSTOMER_SAID_RE.test(caller[i]) && caller[i].split(/\s+/).length >= 2) return caller[i].slice(0, 200);
  }
  for (let i = caller.length - 1; i >= 0; i -= 1) {
    if (caller[i].split(/\s+/).length >= 3) return caller[i].slice(0, 200);
  }
  return '';
}

// The call a request came from: the API's ``call_sid`` (rows are keyed by it), else the request id.
export const sourceCallSid = (row) => String(row?.call_sid || row?.request_id || '').trim();

// td423 (Roger 23:28, CAb1577e5c): a second accepting call from the same phone attaches to the OPEN
// request (requests=2, last_requested_at moved, ``source_calls`` = [owner, newest...]). The "Llamada"
// tab must open the NEWEST call (the one the customer just made), with the earlier ones one click away.
export function sourceCallSids(row) {
  const out = [];
  const push = (sid) => { const s = String(sid || '').trim(); if (s && !out.includes(s)) out.push(s); };
  push(sourceCallSid(row));
  (Array.isArray(row?.source_calls) ? row.source_calls : []).forEach(push);
  return out;
}
export const latestSourceCallSid = (row) => { const all = sourceCallSids(row); return all.length ? all[all.length - 1] : ''; };

// WhatsApp partition for the thread tab. The row's department wins, then the agent's department
// (detail API), then a postventa source. Oct 18 (Roger 23:34): a call-back request IS postventa
// work (service hours / advisor call-back), so a row with no department at all opens the
// customer's POSTVENTA thread -- never the legacy ventas-bot thread ("Carina… Cita confirmada").
export function whatsappDepartment(row) {
  const dept = String(row?.department || '').trim().toLowerCase();
  if (dept) return dept;
  const agentDept = String(row?.agent_department || '').trim().toLowerCase();
  if (agentDept === 'postventa') return 'postventa';
  if (agentDept) return 'ventas';
  return 'postventa';
}
